import React, { useState, useEffect, useRef } from "react";
import { inteligenciaMercado, concorrentes } from "../../lib/api";
import { DataTable } from "../shared/DataTable";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Plus, Search, Pencil, Trash2, TrendingUp, Filter, Paperclip, Download, FileCheck, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { formatCurrency, formatDate } from "../../lib/utils";

const TIPOS_ENTREGA = [
  { value: "FOB", label: "FOB - Free on Board" },
  { value: "CIF", label: "CIF - Cost, Insurance and Freight" },
  { value: "DDP", label: "DDP - Delivered Duty Paid" },
];

export function InteligenciaMercadoPage() {
  const [data, setData] = useState([]);
  const [concorrentesList, setConcorrentesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterConcorrente, setFilterConcorrente] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const { isAdmin } = useAuth();
  const anexoInputRef = useRef(null);
  const [uploadingAnexo, setUploadingAnexo] = useState(null);

  const [formData, setFormData] = useState({
    produto_descricao: "",
    concorrente_id: "",
    valor_unitario: 0,
    tipo_entrega: "FOB",
    data_informacao: new Date().toISOString().split("T")[0],
    observacoes: "",
  });

  const fetchData = async () => {
    try {
      const [itemsRes, concorrentesRes] = await Promise.all([
        inteligenciaMercado.list(),
        concorrentes.list(),
      ]);
      setData(itemsRes.data);
      setConcorrentesList(concorrentesRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.produto_descricao.trim()) {
      toast.error("Informe a descrição do produto");
      return;
    }
    if (!formData.concorrente_id) {
      toast.error("Selecione o concorrente");
      return;
    }
    if (formData.valor_unitario <= 0) {
      toast.error("Informe um valor unitário válido");
      return;
    }

    try {
      if (editingItem) {
        await inteligenciaMercado.update(editingItem.id, formData);
        toast.success("Registro atualizado!");
      } else {
        await inteligenciaMercado.create(formData);
        toast.success("Registro criado!");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      produto_descricao: item.produto_descricao,
      concorrente_id: item.concorrente_id,
      valor_unitario: item.valor_unitario,
      tipo_entrega: item.tipo_entrega || "FOB",
      data_informacao: item.data_informacao,
      observacoes: item.observacoes || "",
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      produto_descricao: "",
      concorrente_id: "",
      valor_unitario: 0,
      tipo_entrega: "FOB",
      data_informacao: new Date().toISOString().split("T")[0],
      observacoes: "",
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja realmente excluir este registro?")) return;
    try {
      await inteligenciaMercado.delete(id);
      toast.success("Registro excluído!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao excluir");
    }
  };

  const handleAnexoUpload = async (itemId, file) => {
    if (!file) return;
    setUploadingAnexo(itemId);
    try {
      await inteligenciaMercado.uploadAnexo(itemId, file);
      toast.success("Anexo salvo com sucesso!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao enviar anexo");
    } finally {
      setUploadingAnexo(null);
    }
  };

  const handleAnexoDownload = async (itemId, filename) => {
    try {
      const response = await inteligenciaMercado.downloadAnexo(itemId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'anexo');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Erro ao baixar anexo");
    }
  };

  const handleAnexoDelete = async (itemId) => {
    try {
      await inteligenciaMercado.deleteAnexo(itemId);
      toast.success("Anexo removido!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao remover anexo");
    }
  };

  const filteredData = data.filter((item) => {
    const matchesSearch =
      item.produto_descricao?.toLowerCase().includes(search.toLowerCase()) ||
      item.concorrente_nome?.toLowerCase().includes(search.toLowerCase());
    const matchesConcorrente = filterConcorrente === "all" || item.concorrente_id === filterConcorrente;
    return matchesSearch && matchesConcorrente;
  });

  // Ordenar por data mais recente
  const sortedData = [...filteredData].sort((a, b) => 
    new Date(b.data_informacao) - new Date(a.data_informacao)
  );

  const getTipoEntregaLabel = (tipo) => {
    const found = TIPOS_ENTREGA.find(t => t.value === tipo);
    return found ? found.value : tipo;
  };

  const getTipoEntregaBadgeColor = (tipo) => {
    switch (tipo) {
      case "FOB": return "bg-blue-100 text-blue-800";
      case "CIF": return "bg-green-100 text-green-800";
      case "DDP": return "bg-purple-100 text-purple-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const columns = [
    { 
      key: "produto_descricao", 
      header: "Produto",
      render: (v) => (
        <span className="font-medium truncate max-w-[250px] block" title={v}>
          {v}
        </span>
      )
    },
    { 
      key: "concorrente_nome", 
      header: "Concorrente",
      render: (v) => <span className="text-orange-600 font-medium">{v || "-"}</span>
    },
    { 
      key: "valor_unitario", 
      header: "Valor Unitário", 
      render: (v) => <span className="font-semibold">{formatCurrency(v)}</span>
    },
    { 
      key: "tipo_entrega", 
      header: "Entrega",
      render: (v) => (
        <span className={`px-2 py-1 rounded text-xs font-medium ${getTipoEntregaBadgeColor(v)}`}>
          {getTipoEntregaLabel(v)}
        </span>
      )
    },
    { 
      key: "data_informacao", 
      header: "Data Info", 
      render: (v) => formatDate(v)
    },
    { 
      key: "observacoes", 
      header: "Observações",
      render: (v) => (
        <span className="text-muted-foreground text-sm truncate max-w-[150px] block" title={v}>
          {v || "-"}
        </span>
      )
    },
    {
      key: "actions",
      header: "",
      width: "160px",
      render: (_, row) =>
        isAdmin() && (
          <div className="flex gap-1 items-center">
            {/* Anexo */}
            {row.tem_anexo ? (
              <div className="flex items-center gap-0.5">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  title={`Baixar: ${row.anexo_nome}`}
                  onClick={() => handleAnexoDownload(row.id, row.anexo_nome)}
                  data-testid={`download-anexo-${row.id}`}
                >
                  <Download className="h-4 w-4 text-green-600" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  title="Remover anexo"
                  onClick={() => handleAnexoDelete(row.id)}
                  data-testid={`delete-anexo-${row.id}`}
                >
                  <X className="h-3 w-3 text-red-500" />
                </Button>
              </div>
            ) : (
              <>
                <input
                  type="file"
                  id={`anexo-${row.id}`}
                  className="hidden"
                  accept=".pdf,.png,.jpg,.jpeg,.xlsx,.xls,.doc,.docx"
                  onChange={(e) => {
                    handleAnexoUpload(row.id, e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  title="Anexar comprovante"
                  disabled={uploadingAnexo === row.id}
                  onClick={() => document.getElementById(`anexo-${row.id}`)?.click()}
                  data-testid={`attach-btn-${row.id}`}
                >
                  {uploadingAnexo === row.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Paperclip className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleEdit(row)}
              data-testid={`edit-${row.id}`}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleDelete(row.id)}
              data-testid={`delete-${row.id}`}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        ),
    },
  ];

  // Estatísticas
  const stats = {
    totalRegistros: filteredData.length,
    concorrentesAtivos: new Set(filteredData.map(i => i.concorrente_id)).size,
    produtosUnicos: new Set(filteredData.map(i => i.produto_descricao?.toLowerCase())).size,
  };

  return (
    <div className="space-y-6" data-testid="inteligencia-mercado-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold flex items-center gap-2">
            <TrendingUp className="h-8 w-8 text-orange-600" />
            Inteligência de Mercado
          </h1>
          <p className="text-muted-foreground">Monitoramento de preços da concorrência</p>
        </div>
        {isAdmin() && (
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-inteligencia-btn">
                <Plus className="h-4 w-4 mr-2" />
                Novo Registro
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingItem ? "Editar Registro" : "Novo Registro de Preço"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Produto *</Label>
                  <Input
                    value={formData.produto_descricao}
                    onChange={(e) => setFormData({ ...formData, produto_descricao: e.target.value })}
                    placeholder="Descrição do produto"
                    required
                    data-testid="produto-descricao"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Concorrente *</Label>
                  <Select value={formData.concorrente_id} onValueChange={(v) => setFormData({ ...formData, concorrente_id: v })}>
                    <SelectTrigger data-testid="concorrente-select">
                      <SelectValue placeholder="Selecione o concorrente" />
                    </SelectTrigger>
                    <SelectContent>
                      {concorrentesList.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Valor Unitário *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.valor_unitario}
                      onChange={(e) => setFormData({ ...formData, valor_unitario: parseFloat(e.target.value) || 0 })}
                      required
                      data-testid="valor-unitario"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo de Entrega *</Label>
                    <Select value={formData.tipo_entrega} onValueChange={(v) => setFormData({ ...formData, tipo_entrega: v })}>
                      <SelectTrigger data-testid="tipo-entrega-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TIPOS_ENTREGA.map((tipo) => (
                          <SelectItem key={tipo.value} value={tipo.value}>{tipo.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Data da Informação *</Label>
                  <Input
                    type="date"
                    value={formData.data_informacao}
                    onChange={(e) => setFormData({ ...formData, data_informacao: e.target.value })}
                    required
                    data-testid="data-informacao"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Observações</Label>
                  <Input
                    value={formData.observacoes}
                    onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                    placeholder="Observações adicionais..."
                    data-testid="observacoes"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }}>
                    Cancelar
                  </Button>
                  <Button type="submit" data-testid="save-btn">
                    {editingItem ? "Atualizar" : "Criar"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-sm border p-4">
          <p className="text-sm text-muted-foreground">Total de Registros</p>
          <p className="text-2xl font-heading font-bold">{stats.totalRegistros}</p>
        </div>
        <div className="bg-white rounded-sm border p-4">
          <p className="text-sm text-muted-foreground">Concorrentes Monitorados</p>
          <p className="text-2xl font-heading font-bold text-orange-600">{stats.concorrentesAtivos}</p>
        </div>
        <div className="bg-white rounded-sm border p-4">
          <p className="text-sm text-muted-foreground">Produtos Analisados</p>
          <p className="text-2xl font-heading font-bold text-blue-600">{stats.produtosUnicos}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por produto ou concorrente..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="search-input"
          />
        </div>
        <Select value={filterConcorrente} onValueChange={setFilterConcorrente}>
          <SelectTrigger className="w-48" data-testid="filter-concorrente">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Concorrente" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos Concorrentes</SelectItem>
            {concorrentesList.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DataTable 
        columns={columns} 
        data={sortedData} 
        loading={loading} 
        emptyMessage="Nenhum registro de inteligência de mercado encontrado" 
      />
    </div>
  );
}
